import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
  useTheme,
  IconButton,
  Stack,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
// ✅ Import encoder
import { encodeId } from '../utils/idEncoder';

type Props = {
  title: string;
  vehicleClassIds?: number[];
  navigateTo: string;
  vehicle: any[];
};

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
  }).format(price);

// --- Styled Tags ---
const VehicleSoldTag = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 12,
      left: 12,
      bgcolor: '#ef5350',
      color: '#fff',
      px: 1.5,
      py: 0.5,
      fontWeight: 700,
      fontSize: '0.7rem',
      borderRadius: '20px',
      boxShadow: '0 4px 10px rgba(239, 83, 80, 0.4)',
      zIndex: 2,
      letterSpacing: 0.5,
    }}
  >
    SOLD
  </Box>
);

const VehicleViewCountTag = ({ count }: { count: number }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 12,
      right: 12,
      bgcolor: 'rgba(255, 255, 255, 0.85)',
      color: '#1a1a1a',
      px: 1.2,
      py: 0.5,
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: 0.8,
      fontSize: '0.75rem',
      fontWeight: 700,
      backdropFilter: 'blur(6px)',
      zIndex: 2,
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    }}
  >
    <VisibilityIcon sx={{ fontSize: 16, color: '#FFA100' }} />
    {count.toLocaleString()}
  </Box>
);

const VehicleExploreSection = ({ title, vehicleClassIds, navigateTo, vehicle }: Props) => {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false); // Track hover state for pausing

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Check if we have enough items to warrant scrolling behavior
  const shouldScroll = vehicles.length > 3;

  useEffect(() => {
    // Sort logic
    const sorted = [...vehicle];
    if (tabIndex === 0) {
      sorted.sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0));
    } else if (tabIndex === 1) {
      sorted.sort((a, b) => new Date(b.addDate).getTime() - new Date(a.addDate).getTime());
    }
    setVehicles(sorted);
    setLoading(false);
  }, [tabIndex, vehicle]);

  // --- INFINITE SCROLL ANIMATION LOGIC ---
  useEffect(() => {
    const container = scrollRef.current;
    
    // Logic ensures we only scroll if we have enough items
    if (!container || loading || isHovered || !shouldScroll) return;

    let animationFrameId: number;

    const autoScroll = () => {
      // 1. Move scroll position slightly
      container.scrollLeft += 1.5; // Adjust speed here (higher = faster)

      // 2. Check if we've scrolled past the first set of items (halfway point)
      if (container.scrollLeft >= (container.scrollWidth / 2)) {
         // 3. Reset to start instantly (seamless loop)
         container.scrollLeft = 0; 
      }
      
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [loading, isHovered, vehicles, shouldScroll]); 

  const scrollBy = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };

  const formatDate = (date?: string) => (date ? dayjs(date).format('DD MMM YYYY') : '');

  // Only duplicate the list if 'shouldScroll' is true
  const displayVehicles = loading 
    ? [...Array(4)] 
    : (shouldScroll ? [...vehicles, ...vehicles] : vehicles);

  return (
    <Box sx={{ py: 6, px: { xs: 2, md: 0 } }} maxWidth="lg" mx="auto">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ color: 'text.primary', letterSpacing: '-0.5px' }}>
          {title}
        </Typography>
        <Button
          onClick={() => navigate(navigateTo)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.9rem',
          }}
        >
          View All
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        sx={{
          mb: 0,
          '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3, borderRadius: 1.5 },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            color: 'text.secondary',
            '&.Mui-selected': { color: 'text.primary', fontWeight: 800 },
          },
        }}
      >
        <Tab label="Most Popular" />
        <Tab label="Newly Listed" />
      </Tabs>

      {/* --- CAROUSEL AREA --- */}
      <Box 
        sx={{ position: 'relative' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        
        {/* Scrollable List */}
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 3,
            overflowX: 'auto', 
            py: 8, 
            px: 2,

            // ✅ UPDATED: Always start from left (flex-start), removed condition that centered items
            justifyContent: 'flex-start',

            // Only show fade mask if we are scrolling (many items)
            maskImage: shouldScroll 
                ? 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' 
                : 'none',
            WebkitMaskImage: shouldScroll 
                ? 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' 
                : 'none',

            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {displayVehicles.map((vehicle, i) =>
            loading || !vehicle ? (
              <Box key={i} sx={{ flex: '0 0 auto', width: isSmallScreen ? 280 : 300 }}>
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 4, mb: 1 }} />
                <Skeleton width="60%" height={24} />
                <Skeleton width="40%" height={20} />
              </Box>
            ) : (
              <Box
                // Use index in key because we might duplicate the list
                key={`${vehicle.vehicleId}-${i}`}
                sx={{
                  flex: '0 0 auto',
                  width: isSmallScreen ? 280 : 300,
                }}
              >
                <RouterLink 
                    to={`/vehicle-details/${encodeId(vehicle.vehicleId)}`} 
                    style={{ textDecoration: 'none' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: '24px',
                      border: 'none',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.08)', 
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: '#fff',
                      '&:hover': {
                        transform: 'translateY(-15px) scale(1.02)',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                        zIndex: 10,
                        '& .vehicle-image': { transform: 'scale(1.08)' },
                      },
                    }}
                  >
                    {vehicle?.statusId === 'VSL' && <VehicleSoldTag />}
                    
                    {vehicle?.racingAd && vehicle?.statusId !== 'VSL' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          px: 1.2,
                          py: 0.4,
                          borderRadius: '12px',
                          boxShadow: '0 4px 10px rgba(255, 165, 0, 0.4)',
                          zIndex: 2,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Premium
                      </Box>
                    )}

                    {/* Image Area */}
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4 / 3',
                        position: 'relative',
                        bgcolor: '#fff',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        className="vehicle-image"
                        src={vehicle.vehicleImageUrl || 'https://via.placeholder.com/600x400?text=No+Image'}
                        alt={vehicle.vehicleModel || 'Vehicle'}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain', 
                          transition: 'transform 0.6s ease',
                        }}
                      />
                      
                      {vehicle.viewCount != null && <VehicleViewCountTag count={vehicle.viewCount} />}
                    </Box>

                    <CardContent sx={{ p: 2.5, pt: 2 }}>
                      <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                        {formatPrice(vehicle.askingPrice)}
                      </Typography>

                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          color: 'text.secondary',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                                                textTransform: 'uppercase', // ✅ Forces text to be ALL CAPS
                          overflow: 'hidden',
                          mb: 1.5,
                          fontSize: '0.95rem',
                        }}
                      >
                        {vehicle.vehicleBrand} {vehicle.vehicleModelName}
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        {[
                          vehicle.firstRegistrationDate ? formatDate(vehicle.firstRegistrationDate) : null,
                          vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : null,
                          vehicle.vehicleBodyType,
                        ]
                          .filter(Boolean)
                          .slice(0, 3)
                          .map((tag, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                px: 1,
                                py: 0.4,
                                borderRadius: '8px',
                              }}
                            >
                              {tag}
                            </Box>
                          ))}
                      </Box>

                      {vehicle?.showDealerInVehicleList && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color="primary.main"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {vehicle.dealerName}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </RouterLink>
              </Box>
            )
          )}
        </Box>

        {/* --- NAVIGATION CONTROLS (Only show if scrolling) --- */}
        {shouldScroll && (
            <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="flex-end" 
                mt={-2} 
                px={2} 
                sx={{ display: { xs: 'none', md: 'flex' } }}
            >
                <IconButton
                    onClick={() => scrollBy(-320)}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                            bgcolor: 'primary.main', 
                            color: '#fff', 
                            transform: 'scale(1.1)',
                            boxShadow: 4
                        },
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>

                <IconButton
                    onClick={() => scrollBy(320)}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                            bgcolor: 'primary.main', 
                            color: '#fff', 
                            transform: 'scale(1.1)',
                            boxShadow: 4
                        },
                    }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Stack>
        )}

      </Box>
    </Box>
  );
};

export default VehicleExploreSection;